/**
 * Enhanced Document Generation Page
 * Uses AI-generated dynamic forms and document requirements from database
 */
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import {
  Button,
  H2,
  Input,
  Textarea,
  Select,
  CountryPicker,
} from "@/shared/ui";
import { ROUTES, getDocumentTypes, type DocumentField } from "@/shared/config";
import { getCountry, type CountryConfig } from "@/shared/config/countries";
import {
  generateDocumentFields,
  generateEnhancedDocument,
  type GenerateFieldsResult,
  type EnhancedGeneratedDocument,
} from "@/shared/ai";
import { improveDocumentWithRequirements } from "@/shared/ai/contract-generation";
import { useAuth } from "@/contexts/AuthContext";
import { usePlanLimits } from "@/hooks/usePlanLimits";
import { useDocuments } from "@/hooks/useDocuments";
import { hasCredits, decrementCredits, incrementUsage } from "@/shared/db";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  Sparkles,
  Save,
  FileText,
  CheckCircle2,
  Globe,
  Info,
  AlertCircle,
  Loader2,
  Copy,
  Eye,
  RefreshCw,
  Edit3,
  Check,
  X,
} from "lucide-react";
import { analytics } from "@/utils/analytics";

type Step = 1 | 2 | 3 | 4 | 5;

export const EnhancedGeneratePage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, profile, refreshProfile } = useAuth();
  const { addDocument } = useDocuments();
  const { checkLimit } = usePlanLimits();
  const language = (i18n.language as "es" | "en") || "es";

  // Steps
  const [currentStep, setCurrentStep] = useState<Step>(1);

  // Form data
  const [documentType, setDocumentType] = useState<string>("");
  const [countryCode, setCountryCode] = useState<string>("");
  const [country, setCountry] = useState<CountryConfig | null>(null);
  const [title, setTitle] = useState<string>("");

  // Dynamic fields from AI
  const [dynamicFields, setDynamicFields] = useState<DocumentField[]>([]);
  const [fieldsResult, setFieldsResult] = useState<GenerateFieldsResult | null>(
    null,
  );
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [isLoadingFields, setIsLoadingFields] = useState(false);

  // Generated document
  const [generatedDoc, setGeneratedDoc] =
    useState<EnhancedGeneratedDocument | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  // Review state
  const [isImproving, setIsImproving] = useState(false);
  const [improveFeedback, setImproveFeedback] = useState("");
  const [showImproveInput, setShowImproveInput] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState("");

  // Saving
  const [isSaving, setIsSaving] = useState(false);

  // Load country data when selected
  useEffect(() => {
    const loadCountry = async () => {
      if (!countryCode) {
        setCountry(null);
        return;
      }
      const countryData = await getCountry(countryCode);
      setCountry(countryData);
    };
    loadCountry();
  }, [countryCode]);

  // Generate dynamic fields when document type and country are selected
  const loadDynamicFields = useCallback(async () => {
    if (!documentType || !country) return;

    setIsLoadingFields(true);
    try {
      const docTypeName =
        getDocumentTypes(language).find((d) => d.id === documentType)?.name ||
        documentType;
      const docTypeDesc = getDocumentTypes(language).find(
        (d) => d.id === documentType,
      )?.description;

      const result = await generateDocumentFields({
        documentType,
        documentTypeName: docTypeName,
        documentTypeDescription: docTypeDesc,
        country,
        language,
      });

      setDynamicFields(result.fields);
      setFieldsResult(result);

      // Initialize form data
      const initialData: Record<string, string> = {};
      result.fields.forEach((f) => (initialData[f.key] = ""));
      setFormData(initialData);

      if (result.metadata.hasLegalFramework) {
        const countryName = language === "es" ? country.nameEs : country.nameEn;
        toast.success(
          t("generate.messages.formGeneratedWithRequirements", {
            country: countryName,
          }),
        );
      }
    } catch (error) {
      toast.error(t("generate.messages.errorLoadingFields"));
    } finally {
      setIsLoadingFields(false);
    }
  }, [documentType, country, language, t]);

  // Handle generate document
  const handleGenerate = async () => {
    if (!user || !profile) {
      toast.error(t("common.loginRequired"));
      return;
    }

    try {
      const canGenerate = await checkLimit("document_generation");
      if (!canGenerate) {
        toast.error(t("generate.messages.limitReached"));
        navigate(ROUTES.PRICING);
        return;
      }

      const hasEnoughCredits = await hasCredits(user.id);
      if (!hasEnoughCredits) {
        toast.error(t("generate.messages.insufficientCredits"));
        navigate(ROUTES.PRICING);
        return;
      }

      setIsGenerating(true);

      const result = await generateEnhancedDocument({
        documentType,
        countryCode,
        formData,
        language,
      });

      setGeneratedDoc(result);
      setEditedContent(result.content);

      await decrementCredits(user.id);
      await incrementUsage(user.id, "document_generation", 1);
      await refreshProfile();

      analytics.track("document_generated", {
        document_type: documentType,
        country: countryCode,
        has_legal_framework: result.requirements !== null,
        compliance_passed: result.complianceCheck.coversClauses,
      });

      toast.success(t("generate.messages.generatedSuccess"));
      setCurrentStep(4);
    } catch (error) {
      console.error("Error generating document:", error);
      toast.error(
        error instanceof Error
          ? error.message
          : t("generate.messages.generateError"),
      );
    } finally {
      setIsGenerating(false);
    }
  };

  // Handle improve document
  const handleImprove = async () => {
    if (!generatedDoc || !improveFeedback.trim()) return;

    setIsImproving(true);
    try {
      const improvedContent = await improveDocumentWithRequirements(
        generatedDoc.content,
        improveFeedback,
        generatedDoc.requirements,
        language,
      );

      setGeneratedDoc({
        ...generatedDoc,
        content: improvedContent,
      });
      setEditedContent(improvedContent);
      setImproveFeedback("");
      setShowImproveInput(false);
      toast.success(t("generate.messages.documentImproved"));
    } catch (error) {
      console.error("Error improving document:", error);
      toast.error(t("generate.messages.errorImproving"));
    } finally {
      setIsImproving(false);
    }
  };

  // Handle save document
  const handleSave = async (status: "draft" | "final") => {
    if (!user || !generatedDoc) return;

    setIsSaving(true);
    try {
      const contentToSave = isEditing ? editedContent : generatedDoc.content;

      await addDocument({
        user_id: user.id,
        title:
          title || `${documentType}-${new Date().toISOString().split("T")[0]}`,
        document_type: documentType,
        country: countryCode,
        content: contentToSave,
        status,
      });

      analytics.track("document_saved", {
        document_type: documentType,
        country: countryCode,
        status,
      });

      toast.success(
        status === "final"
          ? t("generate.messages.savedSuccess")
          : t("generate.messages.savedAsDraft"),
      );

      navigate(ROUTES.DASHBOARD);
    } catch (error) {
      console.error("Error saving document:", error);
      toast.error(t("generate.messages.saveError"));
    } finally {
      setIsSaving(false);
    }
  };

  // Step navigation
  const handleNext = () => {
    if (currentStep === 2 && documentType && countryCode) {
      loadDynamicFields();
    }
    setCurrentStep((prev) => Math.min(prev + 1, 5) as Step);
  };

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1) as Step);
  };

  // Check if can proceed to next step
  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return !!documentType;
      case 2:
        return !!countryCode;
      case 3:
        return dynamicFields
          .filter((f) => f.required)
          .every((f) => formData[f.key]?.trim());
      case 4:
        return !!generatedDoc;
      default:
        return false;
    }
  };

  // Group fields by category
  const groupedFields = dynamicFields.reduce(
    (acc, field) => {
      const group = field.group || "general";
      if (!acc[group]) acc[group] = [];
      acc[group].push(field);
      return acc;
    },
    {} as Record<string, DocumentField[]>,
  );

  // Get country name based on language
  const getCountryName = () => {
    if (!country) return "";
    return language === "es" ? country.nameEs : country.nameEn;
  };

  return (
    <div className="min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <H2 className="text-3xl font-bold text-tiber-900 mb-2">
            {t("generate.enhanced.title")}
          </H2>
          <p className="text-tiber-700">{t("generate.enhanced.subtitle")}</p>
        </div>

        {/* Progress Steps */}
        <div className="flex justify-center mb-8">
          <div className="flex items-center gap-2">
            {[1, 2, 3, 4, 5].map((step) => (
              <React.Fragment key={step}>
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                    step < currentStep
                      ? "bg-tiber-600 text-white"
                      : step === currentStep
                        ? "bg-tiber-900 text-white ring-4 ring-tiber-200"
                        : "bg-tiber-100 text-tiber-700"
                  }`}
                >
                  {step < currentStep ? (
                    <CheckCircle2 className="w-5 h-5" />
                  ) : (
                    step
                  )}
                </div>
                {step < 5 && (
                  <div
                    className={`w-12 h-1 rounded ${step < currentStep ? "bg-tiber-600" : "bg-gray-200"}`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Step Content */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-6">
          <AnimatePresence mode="wait">
            {/* Step 1: Select Document Type */}
            {currentStep === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <div className="flex items-center gap-3 mb-6">
                  <FileText className="w-6 h-6 text-tiber-600" />
                  <h3 className="text-xl font-bold text-juridico">
                    {t("generate.enhanced.step1Title")}
                  </h3>
                </div>

                <Select
                  label={t("generate.enhanced.selectDocumentType")}
                  placeholder={t(
                    "generate.enhanced.selectDocumentTypePlaceholder",
                  )}
                  value={documentType}
                  onChange={(e) => setDocumentType(e.target.value)}
                  options={getDocumentTypes(language).map((type) => ({
                    value: type.id,
                    label: type.name || type.nameEs,
                  }))}
                />

                {documentType && (
                  <div className="mt-4 p-4 bg-tiber-50 rounded-lg border border-tiber-200">
                    <p className="text-sm text-tiber-800">
                      {
                        getDocumentTypes(language).find(
                          (docType) => docType.id === documentType,
                        )?.description
                      }
                    </p>
                  </div>
                )}
              </motion.div>
            )}

            {/* Step 2: Select Country */}
            {currentStep === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <div className="flex items-center gap-3 mb-6">
                  <Globe className="w-6 h-6 text-tiber-600" />
                  <h3 className="text-xl font-bold text-juridico">
                    {t("generate.enhanced.step2Title")}
                  </h3>
                </div>

                <CountryPicker
                  label={t("generate.enhanced.selectCountry")}
                  value={countryCode}
                  onChange={setCountryCode}
                />

                {country && (
                  <div className="mt-4 p-4 bg-tiber-50 rounded-lg border border-tiber-200">
                    <div className="flex items-start gap-3">
                      <Info className="w-5 h-5 text-tiber-600 shrink-0 mt-0.5" />
                      <div>
                        <p className="font-semibold text-tiber-900">
                          {getCountryName()}
                        </p>
                        <p className="text-sm text-tiber-700 mt-1">
                          {language === "es"
                            ? country.legalSystemEs
                            : country.legalSystemEn}
                        </p>
                        <p className="text-sm text-tiber-600 mt-1">
                          {t("generate.enhanced.currency")}:{" "}
                          {country.currencyCode} (
                          {language === "es"
                            ? country.currencyNameEs
                            : country.currencyNameEn}
                          )
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </motion.div>
            )}

            {/* Step 3: Fill Dynamic Form */}
            {currentStep === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <div className="flex items-center gap-3 mb-6">
                  <Edit3 className="w-6 h-6 text-tiber-600" />
                  <h3 className="text-xl font-bold text-juridico">
                    {t("generate.enhanced.step3Title")}
                  </h3>
                </div>

                {isLoadingFields ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <Loader2 className="w-12 h-12 text-tiber-600 animate-spin mb-4" />
                    <p className="text-tiber-700">
                      {t("generate.enhanced.loadingForm")}
                    </p>
                  </div>
                ) : (
                  <>
                    {fieldsResult?.metadata.hasLegalFramework && (
                      <div className="mb-6 p-4 bg-atlantis-50 rounded-lg border border-atlantis-200">
                        <div className="flex items-start gap-3">
                          <CheckCircle2 className="w-5 h-5 text-atlantis-600 shrink-0 mt-0.5" />
                          <div>
                            <p className="font-semibold text-atlantis-900">
                              {t("generate.enhanced.formWithLegalRequirements")}
                            </p>
                            <p className="text-sm text-atlantis-700 mt-1">
                              {t("generate.enhanced.formIncludesFields", {
                                country: getCountryName(),
                              })}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Title input */}
                    <div className="mb-6">
                      <Input
                        label={t("generate.enhanced.documentTitleLabel")}
                        placeholder={t(
                          "generate.enhanced.documentTitlePlaceholder",
                        )}
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                      />
                    </div>

                    {/* Dynamic fields by group */}
                    {Object.entries(groupedFields).map(([group, fields]) => (
                      <div key={group} className="mb-6">
                        <h4 className="text-lg font-semibold text-tiber-800 mb-4 border-b pb-2">
                          {t(`generate.step3.groupLabels.${group}`, {
                            defaultValue: group,
                          })}
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {fields.map((field) => (
                            <div
                              key={field.key}
                              className={
                                field.type === "textarea" ? "md:col-span-2" : ""
                              }
                            >
                              {field.type === "textarea" ? (
                                <div>
                                  <label className="block mb-2 text-sm font-medium text-juridico">
                                    {field.label}
                                    {field.required && (
                                      <span className="text-red-500 ml-1">
                                        *
                                      </span>
                                    )}
                                  </label>
                                  <Textarea
                                    placeholder={field.placeholder}
                                    value={formData[field.key] || ""}
                                    onChange={(e) =>
                                      setFormData((prev) => ({
                                        ...prev,
                                        [field.key]: e.target.value,
                                      }))
                                    }
                                    rows={3}
                                  />
                                  {field.helpText && (
                                    <p className="text-xs text-titan mt-1 flex items-center gap-1">
                                      <Info className="w-3 h-3" />{" "}
                                      {field.helpText}
                                    </p>
                                  )}
                                </div>
                              ) : (
                                <div>
                                  <Input
                                    label={`${field.label}${field.required ? " *" : ""}`}
                                    placeholder={field.placeholder}
                                    type={field.type}
                                    value={formData[field.key] || ""}
                                    onChange={(e) =>
                                      setFormData((prev) => ({
                                        ...prev,
                                        [field.key]: e.target.value,
                                      }))
                                    }
                                  />
                                  {field.helpText && (
                                    <p className="text-xs text-titan mt-1 flex items-center gap-1">
                                      <Info className="w-3 h-3" />{" "}
                                      {field.helpText}
                                    </p>
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </motion.div>
            )}

            {/* Step 4: Review Generated Document */}
            {currentStep === 4 && (
              <motion.div
                key="step4"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <Eye className="w-6 h-6 text-tiber-600" />
                    <h3 className="text-xl font-bold text-juridico">
                      {t("generate.enhanced.step4Title")}
                    </h3>
                  </div>
                  <span
                    className={`px-3 py-1 rounded-full text-sm font-medium ${
                      generatedDoc?.status === "pending_review"
                        ? "bg-atlantis-100 text-atlantis-900"
                        : "bg-atlantis-100 text-atlantis-900"
                    }`}
                  >
                    {generatedDoc?.status === "pending_review"
                      ? t("generate.enhanced.pendingReview")
                      : t("generate.enhanced.approved")}
                  </span>
                </div>

                {/* Compliance Check */}
                {generatedDoc?.complianceCheck && (
                  <div
                    className={`mb-4 p-4 rounded-lg border ${
                      generatedDoc.complianceCheck.coversClauses
                        ? "bg-atlantis-50 border-atlantis-200"
                        : "bg-yellow-50 border-yellow-200"
                    }`}
                  >
                    <h4 className="font-semibold mb-2">
                      {t("generate.enhanced.complianceCheck.title")}
                    </h4>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="flex items-center gap-2">
                        {generatedDoc.complianceCheck.coversClauses ? (
                          <Check className="w-4 h-4 text-atlantis-600" />
                        ) : (
                          <AlertCircle className="w-4 h-4 text-yellow-600" />
                        )}
                        <span>
                          {t(
                            "generate.enhanced.complianceCheck.requiredClauses",
                          )}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        {generatedDoc.complianceCheck.usesCorrectCurrency ? (
                          <Check className="w-4 h-4 text-atlantis-600" />
                        ) : (
                          <X className="w-4 h-4 text-red-600" />
                        )}
                        <span>
                          {t(
                            "generate.enhanced.complianceCheck.correctCurrency",
                          )}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        {generatedDoc.complianceCheck.referencesLaws ? (
                          <Check className="w-4 h-4 text-atlantis-600" />
                        ) : (
                          <X className="w-4 h-4 text-red-600" />
                        )}
                        <span>
                          {t(
                            "generate.enhanced.complianceCheck.legalReferences",
                          )}
                        </span>
                      </div>
                    </div>
                    {generatedDoc.complianceCheck.missingClauses.length > 0 && (
                      <div className="mt-2 text-sm text-yellow-800">
                        <p className="font-medium">
                          {t(
                            "generate.enhanced.complianceCheck.missingClauses",
                          )}
                        </p>
                        <ul className="list-disc pl-4 mt-1">
                          {generatedDoc.complianceCheck.missingClauses
                            .slice(0, 3)
                            .map((clause, i) => (
                              <li key={i}>{clause}</li>
                            ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {/* Document Content */}
                <div className="relative">
                  {isEditing ? (
                    <Textarea
                      value={editedContent}
                      onChange={(e) => setEditedContent(e.target.value)}
                      rows={20}
                      className="font-mono text-sm"
                    />
                  ) : (
                    <div className="bg-romance-50 rounded-lg p-6 max-h-[500px] overflow-y-auto border">
                      <pre className="whitespace-pre-wrap font-sans text-sm text-tiber-800">
                        {generatedDoc?.content}
                      </pre>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-3 mt-4">
                  <Button
                    variant="secondary"
                    onClick={() => {
                      navigator.clipboard.writeText(
                        generatedDoc?.content || "",
                      );
                      toast.success(t("generate.messages.copied"));
                    }}
                  >
                    <Copy className="w-4 h-4" />
                    {t("generate.enhanced.actions.copy")}
                  </Button>

                  <Button
                    variant="secondary"
                    onClick={() => {
                      if (isEditing) {
                        setGeneratedDoc((prev) =>
                          prev ? { ...prev, content: editedContent } : null,
                        );
                      }
                      setIsEditing(!isEditing);
                    }}
                  >
                    <Edit3 className="w-4 h-4" />
                    {isEditing
                      ? t("generate.enhanced.actions.saveEdit")
                      : t("generate.enhanced.actions.edit")}
                  </Button>

                  <Button
                    variant="secondary"
                    onClick={() => setShowImproveInput(!showImproveInput)}
                  >
                    <RefreshCw className="w-4 h-4" />
                    {t("generate.enhanced.actions.improveWithAI")}
                  </Button>
                </div>

                {/* Improve with AI Input */}
                {showImproveInput && (
                  <div className="mt-4 p-4 bg-tiber-50 rounded-lg border border-tiber-200">
                    <label className="block text-sm font-medium text-tiber-900 mb-2">
                      {t("generate.enhanced.actions.whatToImprove")}
                    </label>
                    <Textarea
                      placeholder={t(
                        "generate.enhanced.actions.improvePlaceholder",
                      )}
                      value={improveFeedback}
                      onChange={(e) => setImproveFeedback(e.target.value)}
                      rows={3}
                    />
                    <div className="flex gap-2 mt-2">
                      <Button
                        onClick={handleImprove}
                        disabled={isImproving || !improveFeedback.trim()}
                      >
                        {isImproving ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Sparkles className="w-4 h-4" />
                        )}
                        {t("generate.enhanced.actions.applyImprovements")}
                      </Button>
                      <Button
                        variant="ghost"
                        onClick={() => setShowImproveInput(false)}
                      >
                        {t("generate.enhanced.actions.cancel")}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Warnings and Legal Notes */}
                {generatedDoc?.warnings && generatedDoc.warnings.length > 0 && (
                  <div className="mt-4 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                    <h4 className="font-semibold text-yellow-900 mb-2 flex items-center gap-2">
                      <AlertCircle className="w-5 h-5" />
                      {t("generate.enhanced.actions.warnings")}
                    </h4>
                    <ul className="list-disc pl-4 text-sm text-yellow-800">
                      {generatedDoc.warnings.map((w, i) => (
                        <li key={i}>{w}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </motion.div>
            )}

            {/* Step 5: Save */}
            {currentStep === 5 && (
              <motion.div
                key="step5"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="text-center py-8"
              >
                <CheckCircle2 className="w-16 h-16 text-atlantis-600 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-juridico mb-2">
                  {t("generate.enhanced.step5Title")}
                </h3>
                <p className="text-tiber-700 mb-6">
                  {t("generate.enhanced.step5Subtitle")}
                </p>
                <div className="flex justify-center gap-4">
                  <Button
                    variant="secondary"
                    onClick={() => handleSave("draft")}
                    disabled={isSaving}
                  >
                    <Save className="w-4 h-4" />
                    {t("generate.enhanced.actions.saveAsDraft")}
                  </Button>
                  <Button
                    onClick={() => handleSave("final")}
                    disabled={isSaving}
                  >
                    {isSaving ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Check className="w-4 h-4" />
                    )}
                    {t("generate.enhanced.actions.saveAsFinal")}
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Navigation */}
        <div className="flex justify-between">
          <Button
            variant="ghost"
            onClick={handleBack}
            disabled={currentStep === 1}
          >
            <ArrowLeft className="w-4 h-4" />
            {t("generate.enhanced.actions.back")}
          </Button>

          {currentStep < 4 ? (
            currentStep === 3 ? (
              <Button
                onClick={handleGenerate}
                disabled={!canProceed() || isGenerating}
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t("generate.enhanced.actions.generating")}
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    {t("generate.enhanced.actions.generateDocument")}
                  </>
                )}
              </Button>
            ) : (
              <Button onClick={handleNext} disabled={!canProceed()}>
                {t("generate.enhanced.actions.next")}
                <ArrowRight className="w-4 h-4" />
              </Button>
            )
          ) : currentStep === 4 ? (
            <Button onClick={() => setCurrentStep(5)}>
              {t("generate.enhanced.actions.approveAndContinue")}
              <ArrowRight className="w-4 h-4" />
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default EnhancedGeneratePage;
